import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Settings, Zap, Brain, Wrench, ChevronRight, User } from 'lucide-react'
import { useAuthStore, fadeUp, staggerContainer, staggerItem } from '@personic/shared'

export default function Home() {
  const { user } = useAuthStore()
  const isStaff = user?.role === 'employee' || user?.role === 'superuser'

  const productCards = [
    {
      label: 'Platform Settings',
      description: 'Manage account, team, billing, API keys, and compliance.',
      href: '/settings/account/organization',
      icon: Settings,
      available: true,
    },
    {
      label: 'Engage',
      description: 'Real-time customer intelligence, personas, and session analytics.',
      href: '/engage/dashboard',
      icon: Zap,
      available: true,
    },
    {
      label: 'Coach',
      description: 'Agent coaching, skill development, and performance insights.',
      href: '/coach/dashboard',
      icon: Brain,
      available: false,
    },
  ]

  return (
    <div>
      {/* Welcome */}
      <motion.div variants={fadeUp} initial="hidden" animate="visible" className="mb-8">
        <h1 className="text-2xl font-heading font-bold text-midnight-900">
          Welcome back{user?.name ? `, ${user.name}` : ''}
        </h1>
        <p className="mt-1 text-warm-600 text-sm">
          {isStaff ? 'Personic AI staff portal — manage tenants, tools, and platform operations.' : 'Your Personic AI platform dashboard.'}
        </p>
      </motion.div>

      {/* Product cards */}
      <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {productCards.map((card) => (
          <motion.div key={card.label} variants={staggerItem}>
            {card.available ? (
              <Link
                to={card.href}
                className="block p-5 rounded-xl border border-warm-200 bg-white hover:border-copper-300 hover:shadow-sm transition-all group"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-copper-50 flex items-center justify-center group-hover:bg-copper-100 transition-colors">
                    <card.icon className="w-5 h-5 text-copper-500" />
                  </div>
                  <ChevronRight className="w-4 h-4 text-warm-300 group-hover:text-copper-500" />
                </div>
                <h3 className="font-heading font-semibold text-midnight-800 group-hover:text-copper-700">{card.label}</h3>
                <p className="text-xs text-warm-500 mt-1">{card.description}</p>
              </Link>
            ) : (
              <div className="block p-5 rounded-xl border border-warm-200 bg-warm-50 opacity-60">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-warm-100 flex items-center justify-center">
                    <card.icon className="w-5 h-5 text-warm-400" />
                  </div>
                  <span className="text-[10px] font-semibold text-warm-400 uppercase bg-warm-200 px-2 py-0.5 rounded">Coming Soon</span>
                </div>
                <h3 className="font-heading font-semibold text-warm-600">{card.label}</h3>
                <p className="text-xs text-warm-400 mt-1">{card.description}</p>
              </div>
            )}
          </motion.div>
        ))}
      </motion.div>

      {/* Staff section */}
      {isStaff && (
        <motion.div variants={fadeUp} initial="hidden" animate="visible">
          <h2 className="font-heading font-semibold text-midnight-800 mb-3">Staff Tools</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: 'Tenant Operations', href: '/admin/tenants', icon: Wrench },
              { label: 'Employee Dashboard', href: '/employee/dashboard', icon: User },
              { label: 'System Health', href: '/employee/health', icon: Zap },
              { label: 'Admin Panel', href: '/employee/admin', icon: Settings },
            ].map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="flex items-center gap-3 p-3 rounded-lg border border-warm-200 bg-white hover:border-copper-300 hover:bg-copper-50/50 transition-colors group"
              >
                <link.icon className="w-4 h-4 text-warm-400 group-hover:text-copper-500" />
                <span className="text-sm font-medium text-warm-700 group-hover:text-copper-700">{link.label}</span>
              </Link>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  )
}
